import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLive } from "@/lib/live";
import { useSections } from "@/lib/sections";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass, Ticket, ScanLine, Heart, PartyPopper, UserRound, Store, CalendarCheck, Users, Settings, Building2, Scale, LayoutDashboard, Wallet, ScrollText, Contact, Handshake, Trees, Star, Tags, KeyRound, LucideIcon,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { PendingNotice, SuspendedBanner } from "@/components/PendingNotice";
import { BalanceBanner } from "@/components/BalanceBanner";
import { VenueAlertsProvider, ExpiringBanner, usePendingSummary } from "@/components/VenueAlerts";
import { LoginAlertBanner } from "@/components/LoginAlertBanner";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { Role } from "@/types";
import { TempPasswordBanner } from "@/components/AccountSecurity";

type Item = { to: string; key: string; icon: LucideIcon; id: string; roles?: string[]; short?: string };

const NAV: Record<Role, Item[]> = {
  customer: [
    { to: "/app", key: "nav.home", icon: Compass, id: "home" },
    { to: "/app/spots", key: "nav.spots", icon: Trees, id: "spots" },
    { to: "/app/bookings", key: "nav.bookings", icon: Ticket, id: "bookings" },
    { to: "/app/favorites", key: "nav.favorites", icon: Heart, id: "favorites" },
    { to: "/app/profile", key: "nav.profile", icon: UserRound, id: "profile" },
  ],
  venue: [
    { to: "/venue", key: "nav.myVenues", icon: Store, id: "my-venues" },
    { to: "/venue/events", key: "nav.events", icon: PartyPopper, id: "venue-events" },
    { to: "/venue/checkin", key: "nav.checkin", icon: ScanLine, id: "checkin", roles: ["owner", "reception"], short: "nav.checkinShort" },
    { to: "/venue/bookings", key: "nav.venueBookings", icon: CalendarCheck, id: "venue-bookings" },
    { to: "/venue/reviews", key: "nav.reviews", icon: Star, id: "venue-reviews" },
    { to: "/venue/finance", key: "nav.finance", icon: Wallet, id: "venue-finance", roles: ["owner", "finance"] },
    { to: "/venue/team", key: "nav.team", icon: Users, id: "team" },
    { to: "/venue/settings", key: "nav.settings", icon: Settings, id: "venue-settings" },
  ],
  admin: [
    { to: "/admin/dashboard", key: "nav.dashboard", icon: LayoutDashboard, id: "admin-dashboard" },
    { to: "/admin/partners", key: "nav.adminPartners", icon: Handshake, id: "admin-partners", short: "nav.partnersShort" },
    { to: "/admin", key: "nav.adminPlaces", icon: Building2, id: "admin-venues", short: "nav.venuesShort" },
    { to: "/admin/accounts", key: "nav.accounts", icon: Contact, id: "admin-accounts", short: "nav.accountsShort" },
    { to: "/admin/categories", key: "nav.categories", icon: Tags, id: "admin-categories" },
    { to: "/admin/activations", key: "nav.activations", icon: KeyRound, id: "admin-activations", roles: ["super", "reception"] },
    { to: "/admin/events", key: "nav.events", icon: PartyPopper, id: "admin-events" },
    { to: "/admin/bookings", key: "nav.adminBookings", icon: CalendarCheck, id: "admin-bookings", short: "nav.bookingsShort" },
    { to: "/admin/cases", key: "nav.adminCases", icon: Scale, id: "admin-cases", roles: ["super", "reception"] },
    { to: "/admin/finance", key: "nav.finance", icon: Wallet, id: "admin-finance", roles: ["super", "finance"], short: "nav.financeShort" },
    { to: "/admin/audit", key: "nav.audit", icon: ScrollText, id: "admin-audit", roles: ["super"], short: "nav.auditShort" },
    { to: "/admin/settings", key: "nav.settings", icon: Settings, id: "admin-settings" },
  ],
};

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 rounded-full px-4 h-10 text-sm font-semibold transition-[background-color,color] duration-200 ${
    isActive ? "bg-primary text-primary-foreground shadow-md shadow-primary/25" : "hover:bg-accent text-foreground/80"
  }`;

const ActivationBadge = () => {
  const [n, setN] = useState(0);
  const load = () => api.get("/admin/activations/count").then((r) => setN(r.data.count)).catch(() => {});
  useEffect(() => { load(); }, []);
  useLive(load);
  if (!n) return null;
  return <span data-testid="nav-badge-admin-activations" className="min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-black grid place-items-center animate-in zoom-in">{n}</span>;
};

const Badge = ({ id }: { id: string }) => {
  const s = usePendingSummary();
  if (id === "admin-activations") return <ActivationBadge />;
  if (id !== "venue-bookings" || !s?.count) return null;
  return <span data-testid={`nav-badge-${id}`} className="min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-black grid place-items-center animate-in zoom-in">{s.count}</span>;
};

export const RoleShell = ({ role }: { role: Role }) => {
  const { user } = useAuth();
  const desk = role === "venue" && !!user && ["owner", "reception"].includes(user.venue_role || "") && user.venue_account?.status === "approved";
  return <VenueAlertsProvider enabled={desk}><Shell role={role} desk={desk} /></VenueAlertsProvider>;
};

const Shell = ({ role, desk }: { role: Role; desk: boolean }) => {
  const { t } = usePrefs();
  const { user } = useAuth();
  const location = useLocation();
  const sub = (user && (user.venue_role || user.admin_role)) || "";
  const suspended = role === "venue" && user && user.venue_account?.status === "suspended";
  const sec = useSections();
  const items = NAV[role].filter((i) => (!i.roles || i.roles.includes(sub)) && !(suspended && i.id === "checkin")
    && !(i.id === "spots" && !sec.spots) && !(i.id === "bookings" && !sec.events));
  const dashboard = role !== "customer";
  const vstatus = role === "venue" && user ? user.venue_account?.status : undefined;
  const blocked = (vstatus === "pending" || vstatus === "rejected") && location.pathname !== "/venue/settings";

  const desktopNav = !dashboard && (
    <nav className="flex gap-1">
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end className={linkCls} data-testid={`desktop-nav-${i.id}`}>
          <i.icon className="h-4 w-4" />
          {t(i.key)}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <AppHeader nav={desktopNav} />
      <div className="mx-auto max-w-6xl flex">
        {dashboard && (
          <aside className="hidden md:flex flex-col gap-1 w-60 shrink-0 p-4 sticky top-16 h-[calc(100vh-4rem)] border-e">
            <p className="text-xs font-bold text-muted-foreground px-4 pb-2">{t(`role.${role}`)}</p>
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end className={linkCls} data-testid={`sidebar-nav-${i.id}`}>
                <i.icon className="h-4 w-4" />
                <span className="flex-1">{t(i.key)}</span>
                <Badge id={i.id} />
              </NavLink>
            ))}
          </aside>
        )}
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1 min-w-0 px-4 pt-5 pb-32 md:pb-12"
        >
          {role === "venue" && <TempPasswordBanner />}<LoginAlertBanner />{blocked ? <PendingNotice /> : <>{vstatus === "suspended" && <SuspendedBanner />}{desk && <ExpiringBanner />}{vstatus && ["owner", "finance"].includes((user && user.venue_role) || "") && <BalanceBanner />}<Outlet /></>}
        </motion.main>
      </div>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-3 inset-x-3 z-40 rounded-full border bg-card/85 backdrop-blur-xl shadow-2xl shadow-black/10 p-1.5 flex justify-between overflow-x-auto no-scrollbar"
      >
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end
            data-testid={`bottom-nav-${i.id}`}
            className={({ isActive }) =>
              `flex-1 ${items.length > 6 ? "min-w-[4.1rem]" : "min-w-[3.1rem]"} flex flex-col items-center justify-center gap-0.5 h-14 rounded-full text-[10px] min-[400px]:text-[11px] font-bold transition-[background-color,color] duration-200 ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`
            }
          >
            <span className="relative"><i.icon className="h-5 w-5 shrink-0" /><span className="absolute -top-2 -end-3"><Badge id={i.id} /></span></span>
            <span className="truncate max-w-full px-0.5">{t(i.short || i.key)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
